import type { PropsWithChildren } from 'react';

import type { getFormatter } from '../../utilities';
import styles from './sankey-chart.module.css';
import { getSankeyNodeLabel, type SankeyNodeValidated } from './types';

/** Raw (unscaled) inflow/outflow totals for a node. */
export type SankeyNodeRaw = { in: number; out: number };

export const SankeyNodeTooltipContent = ({
  node,
  format,
  raw,
}: {
  node: SankeyNodeValidated;
  format: ReturnType<typeof getFormatter>;
  raw: SankeyNodeRaw;
}) => {
  const label = getSankeyNodeLabel(node);

  // Role derived from link presence: only-out is a source, only-in a sink.
  const isPureSource = raw.out > 0 && raw.in === 0;
  const isPureSink = raw.in > 0 && raw.out === 0;

  // Flow on one side only is a single transition count, so it takes the same
  // wording the link tooltip uses for that number rather than a direction.
  if (isPureSource || isPureSink) {
    return (
      <Block label={label}>
        <dt>Transitions</dt>
        <dd>{format(isPureSource ? raw.out : raw.in)}</dd>
      </Block>
    );
  }

  return (
    <Block label={label}>
      <dt>Inflow</dt>
      <dd>{format(raw.in)}</dd>
      <dt>Outflow</dt>
      <dd>{format(raw.out)}</dd>
    </Block>
  );
};

const Block = ({ label, children }: PropsWithChildren<{ label: string }>) => (
  <div className={styles.tooltipBlock} data-testid="sankey-chart__node-tooltip">
    <div className={styles.tooltipTitle}>{label}</div>
    <dl className={styles.tooltipMetrics}>{children}</dl>
  </div>
);
